import { resolveCountryOfOrigin } from '@peekapak/country-origin';
import type { HomeCountrySource } from '@peekapak/common/lib/homeCountryResolution';
import { isRetryableCognitoError } from './CognitoErrors';
import { dataResidencyManager } from './DataResidencyManager';
import { resolvePersistedDataResidency, SupportedDataResidency, toSupportedResidency } from './residency';
import { initializeAmplifyForDataResidency } from './DataResidencyAwareAuthHelpers';
import { getDataResidencyRuntime } from './runtime';

// Two sign-in strategies, because the two apps face different users.
//
// apps/frontend (educators, on peekapak.com) uses signInSingleRegion: the page
// carries a home-country control, so the region is resolved deterministically
// and credentials are only ever sent to that one pool. A wrong guess is
// corrected by the user, not by trying elsewhere.
//
// apps/mypeekaville-web (elementary students) uses signInRoundRobin: asking a
// young child to identify their country is not a usable control, so the app
// tries its best-guess region and then the other one. Both pools are Peekapak's
// own, and the alternative - a child locked out with no way to say where they
// are - is worse.

export const SIGN_IN_MAX_ATTEMPTS = 5;
export const SIGN_IN_RETRY_BASE_DELAY_MS = 1000;
export const SIGN_IN_RETRY_MAX_DELAY_MS = 8000;
const SIGN_IN_RETRY_JITTER_MAX_MS = 250;

type SleepFunction = (delayMs: number) => Promise<void>;
type JitterFunction = () => number;

export interface SignInRetryStatus {
  attempt: number;
  maxAttempts: number;
}

export interface IntelligentSignInOptions {
  onRetry?: (status: SignInRetryStatus) => void;
  sleep?: SleepFunction;
  getJitterMs?: JitterFunction;
}

export function getSignInRetryDelayMs(retryIndex: number, jitterMs = 0): number {
  const exponentialDelay = SIGN_IN_RETRY_BASE_DELAY_MS * 2 ** retryIndex;
  return Math.min(exponentialDelay + jitterMs, SIGN_IN_RETRY_MAX_DELAY_MS);
}

function getDefaultSignInJitterMs(): number {
  return Math.floor(Math.random() * SIGN_IN_RETRY_JITTER_MAX_MS);
}

function defaultSignInSleep(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

/**
 * What Amplify hands back from signIn/signUp.
 *
 * Deliberately untyped. Amplify v4 declares these as `any`, and the two apps
 * narrow the result to their own CognitoUserProfileType at the call site;
 * naming it `unknown` here would only move an unchecked cast into both of them.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AmplifyUser = any;

/**
 * A sign-in failure, carrying which region(s) were tried.
 *
 * apps/frontend's sign-in UX needs this: with single-region sign-in a
 * wrong-region guess is indistinguishable from a wrong password, so the message
 * has to be able to point the user at the home-country control.
 */
export interface SignInFailure extends Error {
  originalError?: Error | null;
  dataResidencyAttempted?: SupportedDataResidency;
  dataResidenciesAttempted?: SupportedDataResidency[];
  homeCountry?: string;
  homeCountrySource?: HomeCountrySource;
}

export interface AuthenticationResult {
  user: AmplifyUser;
  dataResidency: SupportedDataResidency;
  attemptCount: number;
  // Where the resolved region came from (chosen vs detected vs default).
  source: HomeCountrySource;
  // The resolved canonical home country (ISO alpha-3) the region derives from.
  homeCountry: string;
}

export interface UserDataResidencyCache {
  email: string;
  dataResidency: SupportedDataResidency;
  lastSuccess: Date;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

/**
 * Per-account memory of the region a sign-in actually succeeded in. No expiry:
 * accounts do not migrate between regions, so a hit stays the best first guess
 * for as long as the browser keeps it.
 */
export class DataResidencyCache {
  private static CACHE_KEY = 'peekapak.userDataResidencyCache';

  static getCache(): UserDataResidencyCache[] {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch (error) {
      console.warn('Failed to load data residency cache:', error);
      return [];
    }
  }

  static cacheSuccessfulLogin(email: string, dataResidency: SupportedDataResidency): void {
    try {
      const cache = this.getCache().filter((c) => c.email !== email);

      cache.push({
        email,
        dataResidency,
        lastSuccess: new Date(),
        confidence: 'HIGH',
      });

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('Failed to cache successful login:', error);
    }
  }

  static getCachedDataResidency(email: string): SupportedDataResidency | null {
    try {
      const userCache = this.getCache().find((c) => c.email === email);
      return userCache ? userCache.dataResidency : null;
    } catch (error) {
      console.warn('Failed to get cached data residency:', error);
      return null;
    }
  }

  static clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      console.warn('Failed to clear data residency cache:', error);
    }
  }

  static clearUserCache(email: string): void {
    try {
      const cache = this.getCache().filter((c) => c.email !== email);
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('Failed to clear user cache:', error);
    }
  }
}

/**
 * Attempt sign-in against ONE region, retrying only transient failures
 * (HTTP 500 / Cognito InternalError) with exponential backoff. A definitive
 * failure - wrong password, user not found - returns immediately, because
 * repeating it changes nothing.
 */
async function attemptSignInInRegion(
  dataResidency: SupportedDataResidency,
  username: string,
  password: string,
  options: IntelligentSignInOptions,
): Promise<{ user: AmplifyUser; attemptCount: number } | { error: Error; attemptCount: number }> {
  const sleep = options.sleep ?? defaultSignInSleep;
  const getJitterMs = options.getJitterMs ?? getDefaultSignInJitterMs;

  await initializeAmplifyForDataResidency(dataResidency);

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= SIGN_IN_MAX_ATTEMPTS; attempt++) {
    try {
      const user = await getDataResidencyRuntime().amplify.signIn(username, password);
      return { user, attemptCount: attempt };
    } catch (error) {
      lastError = error as Error;

      if (!isRetryableCognitoError(error) || attempt >= SIGN_IN_MAX_ATTEMPTS) {
        return { error: lastError, attemptCount: attempt };
      }

      options.onRetry?.({
        attempt: attempt + 1,
        maxAttempts: SIGN_IN_MAX_ATTEMPTS,
      });
      const delayMs = getSignInRetryDelayMs(attempt - 1, getJitterMs());
      console.warn(
        `[SignIn] Transient sign-in error in the ${dataResidency} region ` +
          `(attempt ${attempt}/${SIGN_IN_MAX_ATTEMPTS}); retrying after ${delayMs}ms`,
      );
      await sleep(delayMs);
    }
  }

  return {
    error: lastError ?? new Error('Unknown error'),
    attemptCount: SIGN_IN_MAX_ATTEMPTS,
  };
}

function recordSuccessfulSignIn(username: string, dataResidency: SupportedDataResidency): void {
  DataResidencyCache.cacheSuccessfulLogin(username, dataResidency);
  // The account's real region (authoritative -> persisted).
  void dataResidencyManager.persistDataResidency(dataResidency);
  getDataResidencyRuntime().refreshEndpoints();
}

/**
 * Deterministic single-region sign in - the strategy for apps/frontend.
 *
 * Resolves the country of origin, derives ONE data residency, and attempts
 * authentication against only that region's Cognito pool. There is NO
 * cross-region fallback: a wrong-region guess is corrected by the user changing
 * their home country.
 */
export async function signInSingleRegion(
  username: string,
  password: string,
  options: IntelligentSignInOptions = {},
): Promise<AuthenticationResult> {
  // Sign-in is an auth context, so geolocation prompting is permitted (D14). The
  // resolver still only prompts when no higher-precedence signal exists (D5).
  const origin = await resolveCountryOfOrigin({ mayPromptForGeolocation: true });
  const dataResidency = toSupportedResidency(origin.dataResidency);

  console.log(`🎯 Sign-in region resolved to ${dataResidency} from ${origin.source} (${origin.countryOfOrigin})`);

  const outcome = await attemptSignInInRegion(dataResidency, username, password, options);

  if ('user' in outcome) {
    recordSuccessfulSignIn(username, dataResidency);
    console.log(`✅ Sign in successful: ${dataResidency}`);

    return {
      user: outcome.user,
      dataResidency,
      attemptCount: outcome.attemptCount,
      source: origin.source,
      homeCountry: origin.countryOfOrigin,
    };
  }

  // A wrong-region guess now looks like a failed login, so the sign-in UX must
  // guide the user to check/correct their home country. Attach the resolved
  // region/home country for that messaging.
  const enhancedError: SignInFailure = new Error(
    `Authentication failed in the ${dataResidency} region. Last error: ${outcome.error?.message || 'Unknown error'}`,
  );
  enhancedError.originalError = outcome.error;
  enhancedError.dataResidencyAttempted = dataResidency;
  enhancedError.homeCountry = origin.countryOfOrigin;
  enhancedError.homeCountrySource = origin.source;

  throw enhancedError;
}

/**
 * The order to try regions in, best guess first.
 *
 * Signals, highest confidence first: a region this account has actually signed
 * in from before, then the browser's authoritative residency (explicit choice
 * or a prior successful auth), then the IP guess. Whatever is left over goes
 * last, so every region is still reachable.
 */
export async function getDataResidencyAttemptOrder(username: string): Promise<SupportedDataResidency[]> {
  const preferred =
    DataResidencyCache.getCachedDataResidency(username) ??
    resolvePersistedDataResidency() ??
    (await dataResidencyManager.detectUserDataResidencyFromIP());

  const remaining = Object.values(SupportedDataResidency).filter((residency) => residency !== preferred);

  return [preferred, ...remaining];
}

/**
 * Round-robin sign in - the strategy for apps/mypeekaville-web.
 *
 * Tries the best-guess region first and falls through to the others. Elementary
 * students cannot reasonably be asked which country their data lives in, so the
 * app finds the account rather than making the child find it.
 *
 * Transient failures are retried within the region (the account may well be
 * there); only a definitive failure advances to the next region. On success the
 * region is cached per-account and persisted, so every later visit - and the
 * handoff to the embedded /play build - is a single attempt.
 */
export async function signInRoundRobin(
  username: string,
  password: string,
  options: IntelligentSignInOptions = {},
): Promise<AuthenticationResult> {
  const attemptOrder = await getDataResidencyAttemptOrder(username);

  console.log(`🎯 Sign-in will try regions in order: ${attemptOrder.join(' -> ')}`);

  let lastError: Error | null = null;
  let totalAttempts = 0;

  for (const [index, dataResidency] of attemptOrder.entries()) {
    console.log(`🔐 Attempting sign in: ${dataResidency} (region ${index + 1}/${attemptOrder.length})`);

    const outcome = await attemptSignInInRegion(dataResidency, username, password, options);
    totalAttempts += outcome.attemptCount;

    if ('user' in outcome) {
      recordSuccessfulSignIn(username, dataResidency);
      console.log(`✅ Sign in successful: ${dataResidency}`);

      return {
        user: outcome.user,
        dataResidency,
        attemptCount: totalAttempts,
        source: 'default',
        homeCountry: '',
      };
    }

    lastError = outcome.error;
    console.log(`❌ Sign in failed for ${dataResidency}:`, lastError.message);
  }

  const enhancedError: SignInFailure = new Error(
    `Authentication failed in all data residency options. Last error: ${lastError?.message || 'Unknown error'}`,
  );
  enhancedError.originalError = lastError;
  enhancedError.dataResidenciesAttempted = attemptOrder;

  throw enhancedError;
}

/**
 * Deterministic single-region sign up. Account creation cannot be round-robined
 * - there is no "try the other region" for a create - so both apps resolve ONE
 * region and create there.
 */
export async function intelligentDataResidencySignUp(
  username: string,
  password: string,
  attributes: Record<string, unknown>,
): Promise<AuthenticationResult> {
  const origin = await resolveCountryOfOrigin({ mayPromptForGeolocation: true });
  const dataResidency = toSupportedResidency(origin.dataResidency);

  console.log(
    `📝 Creating new account in the ${dataResidency} region from ${origin.source} (${origin.countryOfOrigin})`,
  );

  await initializeAmplifyForDataResidency(dataResidency);

  const enhancedAttributes = {
    ...attributes,
    'custom:dataResidency': dataResidency,
  };

  const result = await getDataResidencyRuntime().amplify.signUp({
    username,
    password,
    attributes: enhancedAttributes,
  });

  // The account now exists in this region (authoritative -> persisted).
  recordSuccessfulSignIn(username, dataResidency);

  console.log(`✅ Sign up successful in the ${dataResidency} region`);

  return {
    user: result,
    dataResidency,
    attemptCount: 1,
    source: origin.source,
    homeCountry: origin.countryOfOrigin,
  };
}

/**
 * Predict the optimal data residency for a user without authenticating, by
 * delegating to the shared country-of-origin resolver. Used by the
 * forgot-password flow. Geolocation is not prompted here (`false`) - the flow is
 * an email-entry step, not a full auth screen.
 *
 * NOTE: `email` is currently unused - the resolver keys off client-side signals
 * (explicit choice > geolocation > IP), not the typed email.
 */
export async function predictOptimalDataResidency(email: string): Promise<{
  dataResidency: SupportedDataResidency;
  source: HomeCountrySource;
}> {
  void email;
  const origin = await resolveCountryOfOrigin({
    mayPromptForGeolocation: false,
  });
  return {
    dataResidency: toSupportedResidency(origin.dataResidency),
    source: origin.source,
  };
}
